/**
 * The house spring, driven by JavaScript, for the few things CSS cannot move.
 *
 * Almost all motion on this site is a CSS transition on `--rt-ease-spring`
 * (packages/design/src/motion.ts), and that stays the rule. A CSS transition
 * always starts from rest, though, and three kinds of motion cannot:
 *
 *   - a value a hand is holding — the view switch's thumb, dragged and
 *     flicked, has to land with the velocity it was thrown with;
 *   - a value that is retargeted mid-flight — a budget total that changes
 *     again while it is still rolling must keep rolling, not restart;
 *   - a value that is not a CSS property — a rolling digit is ten numerals
 *     repositioned from one number.
 *
 * For those, this steps THE SAME spring (`SPRING`, solved in closed form by
 * `springAt`), so a thrown thumb and a CSS panel settle with one motion.
 * Subscribers write to the DOM directly; nothing here re-renders React per
 * frame. Nothing loops: a spring that has settled stops asking for frames.
 */
import { SPRING, springAt, type SpringSpec } from '@roomtreatment/design';

export interface FrameScheduler {
  now(): number;
  request(callback: () => void): number;
  cancel(id: number): void;
}

const browserScheduler: FrameScheduler = {
  now: () => performance.now(),
  request: (callback) => requestAnimationFrame(() => callback()),
  cancel: (id) => cancelAnimationFrame(id),
};

export interface SpringOptions {
  spec?: SpringSpec;
  /** At rest when within this of the target... */
  restDelta?: number;
  /** ...and moving slower than this (units per second). */
  restSpeed?: number;
  /** Injected in tests; the browser's frame clock otherwise. */
  scheduler?: FrameScheduler;
}

export interface Spring {
  get(): number;
  /** Units per second, right now. */
  getVelocity(): number;
  /** Where it is headed (its current value once at rest). */
  getTarget(): number;
  readonly animating: boolean;
  /** Head for `target` from wherever it is, as fast as it is already moving —
   *  or with `velocity`, for a release that hands over a throw. */
  to(target: number, velocity?: number): void;
  /** Be `value` now, with no motion. */
  jump(value: number): void;
  /** Stop where it is. */
  stop(): void;
  /** Called with every new value, including `jump`. Returns an unsubscribe. */
  onChange(listener: (value: number) => void): () => void;
  /** Called each time it comes to rest after moving. */
  onRest(listener: () => void): () => void;
  /** Stop and drop every listener. */
  destroy(): void;
}

export function createSpring(initial: number, options: SpringOptions = {}): Spring {
  const spec = options.spec ?? SPRING;
  const restDelta = options.restDelta ?? 0.005;
  const restSpeed = options.restSpeed ?? 0.05;
  const clock = options.scheduler ?? browserScheduler;

  let value = initial;
  let velocity = 0;
  let target = initial;
  // The current segment: released at `startMs` from displacement `x0`
  // (relative to `target`) with velocity `v0`.
  let startMs = 0;
  let x0 = 0;
  let v0 = 0;
  let frame: number | null = null;
  const changeListeners = new Set<(value: number) => void>();
  const restListeners = new Set<() => void>();

  const emit = () => {
    for (const listener of changeListeners) listener(value);
  };

  /** Advance the analytic state to `nowMs` without scheduling anything. */
  const sample = (nowMs: number) => {
    const s = springAt(Math.max(0, (nowMs - startMs) / 1000), x0, v0, spec);
    value = target + s.x;
    velocity = s.v;
    return s;
  };

  const tick = () => {
    frame = null;
    const s = sample(clock.now());
    if (Math.abs(s.x) < restDelta && Math.abs(s.v) < restSpeed) {
      value = target;
      velocity = 0;
      emit();
      for (const listener of restListeners) listener();
      return;
    }
    emit();
    frame = clock.request(tick);
  };

  const spring: Spring = {
    get: () => {
      if (frame !== null) sample(clock.now());
      return value;
    },
    getVelocity: () => {
      if (frame !== null) sample(clock.now());
      return velocity;
    },
    getTarget: () => target,
    get animating() {
      return frame !== null;
    },
    to(next, handoff) {
      const now = clock.now();
      if (frame !== null) sample(now);
      target = next;
      startMs = now;
      x0 = value - next;
      v0 = handoff ?? velocity;
      if (Math.abs(x0) < restDelta && Math.abs(v0) < restSpeed) {
        spring.jump(next);
        return;
      }
      if (frame === null) frame = clock.request(tick);
    },
    jump(next) {
      if (frame !== null) clock.cancel(frame);
      frame = null;
      value = next;
      target = next;
      velocity = 0;
      emit();
    },
    stop() {
      if (frame === null) return;
      sample(clock.now());
      clock.cancel(frame);
      frame = null;
      target = value;
      velocity = 0;
    },
    onChange(listener) {
      changeListeners.add(listener);
      return () => changeListeners.delete(listener);
    },
    onRest(listener) {
      restListeners.add(listener);
      return () => restListeners.delete(listener);
    },
    destroy() {
      if (frame !== null) clock.cancel(frame);
      frame = null;
      changeListeners.clear();
      restListeners.clear();
    },
  };
  return spring;
}
